import { useCallback, useRef, useState } from "react";
import { useTranslation } from "../context/TranslationContext";
import { MAX_CHAT_FILE_BYTES, MAX_PENDING_FILES } from "../utils/chatFiles";

let nextId = 0;

/**
 * The files picked for the next message(s), waiting under the box until sent — and
 * sending them, one message each, in order.
 *
 * `sendAll(sendOne)` calls `sendOne(file, index, onProgress)` for each; it resolves true once
 * sent. A failure stops there and leaves that file and the rest waiting, to retry or remove.
 */
export default function useChatFiles({ onError } = {}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState([]);
  const sendingRef = useRef(false);

  const addFiles = useCallback(
    (list) => {
      const files = Array.from(list || []);
      if (!files.length) return;
      const tooBig = files.filter((f) => f.size > MAX_CHAT_FILE_BYTES);
      if (tooBig.length) {
        onError?.(t("chatFiles.tooLarge", { name: tooBig[0].name, max: MAX_CHAT_FILE_BYTES / 1024 / 1024 }));
      }
      const ok = files.filter((f) => f.size <= MAX_CHAT_FILE_BYTES);
      setPending((prev) => {
        const room = MAX_PENDING_FILES - prev.length;
        if (ok.length > room) onError?.(t("chatFiles.tooMany", { max: MAX_PENDING_FILES }));
        return [...prev, ...ok.slice(0, Math.max(0, room)).map((file) => ({ id: ++nextId, file, progress: 0, failed: false }))];
      });
    },
    [onError, t]
  );

  const remove = useCallback((id) => setPending((prev) => prev.filter((p) => p.id !== id)), []);
  const clear = useCallback(() => setPending([]), []);

  const sendAll = useCallback(
    async (sendOne) => {
      if (sendingRef.current) return false;
      sendingRef.current = true;
      const queue = pending;
      try {
        for (let i = 0; i < queue.length; i++) {
          const item = queue[i];
          const update = (patch) => setPending((prev) => prev.map((p) => (p.id === item.id ? { ...p, ...patch } : p)));
          update({ progress: 0, failed: false });
          const ok = await sendOne(item.file, i, (progress) => update({ progress }));
          if (!ok) {
            update({ failed: true, progress: 0 });
            return false;
          }
          setPending((prev) => prev.filter((p) => p.id !== item.id));
        }
        return true;
      } finally {
        sendingRef.current = false;
      }
    },
    [pending]
  );

  return { pending, addFiles, remove, clear, sendAll };
}
